import { Menu, PanelLeft, PanelLeftOpen } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Breadcrumb } from "../components/arc/breadcrumb/breadcrumb";
import { Drawer, DrawerContent } from "../components/arc/drawer/drawer";
import type { NavItem, NavSection } from "./nav";
import { NavLink, NavSections, RailLink, RailSections } from "./nav-list";
import styles from "./shell.module.css";

export type SidebarMode = "expanded" | "rail";

const cx = (...names: (string | undefined)[]) => names.filter(Boolean).join(" ");

export interface AppShellProps {
  /** The top of the sidebar: the company switcher, or a way back. */
  header: (mode: SidebarMode) => ReactNode;
  sections: NavSection[];
  /** Pinned under the groups, above the user (Settings). */
  footerItems?: NavItem[];
  /** The signed-in person, at the bottom. */
  user: (mode: SidebarMode) => ReactNode;
  /** The item of the page on screen. */
  activeId?: string;
  /** Section › page › record. The last crumb is the page on screen; its label titles the phone top bar. */
  breadcrumb: { label: string; href?: string }[];
  /** Right of the top bar: search and help. */
  topBarActions?: ReactNode;
  /** The sidebar folded to a 64 px rail (desktop only). */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  /** Names the menu's landmark and the phone drawer. */
  navLabel?: string;
  children: ReactNode;
}

/**
 * The frame every console page renders in (App Shell, `MHqKw`; spec `Chlwa`): a 256 px sidebar
 * that folds into a 64 px rail, and below 1024 px becomes a 304 px drawer; a content panel
 * inset 8 px with the top bar and the page, where only the page scrolls. Pages render into
 * `children` and draw their own loading, empty, error and no-access states.
 */
export function AppShell({
  header,
  sections,
  footerItems = [],
  user,
  activeId,
  breadcrumb,
  topBarActions,
  collapsed,
  onCollapsedChange,
  navLabel = "Main menu",
  children,
}: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const title = breadcrumb.at(-1)?.label ?? "";

  // a new page closes the phone menu
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeId is the trigger
  useEffect(() => setDrawerOpen(false), [activeId]);

  // Ctrl+B / ⌘B folds the sidebar, as in most editors
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "b") {
        event.preventDefault();
        onCollapsedChange(!collapsed);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [collapsed, onCollapsedChange]);

  // On a phone the account menu sits in the top bar, not in the drawer: its bottom sheet can't be
  // tapped under the drawer, which is modal.
  const expanded = (withUser: boolean) => (
    <>
      <div className={styles.sidebarHeader}>{header("expanded")}</div>
      <NavSections sections={sections} activeId={activeId} label={navLabel} />
      <div className={styles.sidebarFooter}>
        {footerItems.map((item) => (
          <NavLink key={item.id} item={item} active={item.id === activeId} />
        ))}
        {withUser ? user("expanded") : null}
      </div>
    </>
  );

  return (
    <div className={styles.shell}>
      <a className={styles.skip} href="#page">
        Skip to content
      </a>
      <aside className={styles.sidebar} data-collapsed={collapsed ? "" : undefined}>
        {collapsed ? (
          <>
            <div className={styles.railHeader}>{header("rail")}</div>
            <RailSections sections={sections} activeId={activeId} label={navLabel} />
            <div className={styles.railFooter}>
              {footerItems.map((item) => (
                <RailLink key={item.id} item={item} active={item.id === activeId} />
              ))}
              {user("rail")}
            </div>
          </>
        ) : (
          expanded(true)
        )}
      </aside>

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent side="left" title={navLabel} className={styles.drawer}>
          <div className={styles.drawerBody}>{expanded(false)}</div>
        </DrawerContent>
      </Drawer>

      <div className={styles.main}>
        <div className={styles.panel}>
          <header className={styles.topBar}>
            <div className={styles.topBarLeft}>
              <button
                type="button"
                className={cx(styles.iconButton, styles.desktopOnly)}
                onClick={() => onCollapsedChange(!collapsed)}
                aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
                aria-expanded={!collapsed}
              >
                {collapsed ? (
                  <PanelLeftOpen size={18} aria-hidden="true" />
                ) : (
                  <PanelLeft size={18} aria-hidden="true" />
                )}
              </button>
              <button
                type="button"
                className={cx(styles.iconButton, styles.phoneOnly)}
                onClick={() => setDrawerOpen(true)}
                aria-label="Open the menu"
              >
                <Menu size={18} aria-hidden="true" />
              </button>
              <div className={cx(styles.crumbs, styles.desktopOnly)}>
                <Breadcrumb items={breadcrumb} />
              </div>
              <span className={cx(styles.phoneTitle, styles.phoneOnly)}>{title}</span>
            </div>
            <div className={styles.topBarRight}>
              {topBarActions}
              <span className={styles.phoneOnly}>{user("rail")}</span>
            </div>
          </header>
          <main id="page" className={styles.page} tabIndex={-1}>
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
